/**
 * Живая ценовая история для графика: aggregator.py на liquorice-dashboard-backend
 * раз в 2 с выгружает per-market срез из ClickHouse (наши котировки — из WAL
 * бота, Binance — REST-поллинг bookTicker, Uniswap — slot0 раз в блок) в тот
 * же GCS-бакет, что и основной снапшот. Здесь просто поллим его тем же
 * ритмом. Без VITE_LIVE_CHART_BASE_URL (обычный npm run dev) — null, и
 * PriceChart сам падает на синтетику из data/market.ts.
 */
import { useEffect, useState } from 'react'
import type { Market } from '../types'

export interface ChartPoint {
  time: number
  value: number
}

export interface ChartTrade {
  time: number
  notionalUsd: number
  netUsd: number
}

export interface LiveChartData {
  market: Market
  ourBid: ChartPoint[]
  ourAsk: ChartPoint[]
  binanceBid: ChartPoint[]
  binanceAsk: ChartPoint[]
  /** Uniswap has no order book — this is the pool's swap fee applied around
   *  slot0's spot price, i.e. the marginal (near-zero-size) cost on each
   *  side. Not size-aware; see live/uniswap_ingest.py. */
  uniswapBid: ChartPoint[]
  uniswapAsk: ChartPoint[]
  trades: ChartTrade[]
  generatedAtMs: number
}

const CHART_BASE_URL = import.meta.env.VITE_LIVE_CHART_BASE_URL as string | undefined

export function useLiveChart(market: Market): LiveChartData | null {
  const [data, setData] = useState<LiveChartData | null>(null)

  useEffect(() => {
    setData(null)
    if (!CHART_BASE_URL) return
    let stopped = false
    let timer: number | undefined
    const url = `${CHART_BASE_URL}/chart-${market.toLowerCase()}.json`

    const tick = async () => {
      if (stopped) return
      try {
        const res = await fetch(`${url}?_=${Date.now()}`, { cache: 'no-store' })
        if (res.ok) setData(await res.json())
      } catch (err) {
        console.error('[live] chart fetch failed', err)
      } finally {
        if (!stopped) timer = window.setTimeout(tick, 1000)
      }
    }
    tick()
    return () => {
      stopped = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [market])

  return data
}
